// Organizer Schedule page (card a1f0fff5; layout approved by Devin
// 2026-10-05, mockup v12). One board: "To schedule" on the left, one column
// per stage, each competition as code / start / minutes beside level / dance
// and age (dancers) / dancers at a time. Times come from one formula for
// every event (scheduleTiming.js) and follow the live entries. Every change
// saves itself a moment later (Devin 2026-10-05: autosave, with Undo). Organizers
// export the CSV, make their own schedule and upload it as a PDF: there is no
// Publish until Feis.link makes a good PDF itself (card 9d52d24f).
// The admin app carries a copy (back link and import paths differ): keep
// the two in step.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import HeaderOrg from '../../../Headers/HeaderOrg';
import Loader from '../../../../Components/Loader/Loader';
import { serviceConsumer } from '../../../../network/ServiceConsumer';
import { formatDate } from '../../../../helper/formatDate';
import { ShowToastMessage } from '../../../../helper/ShowToastMessage';
import { useMaterialSymbols } from '../../../../Teacher/Oireachtas/components';
import { clockTime, estimate, inputTime, longDuration, parseTime, shortDuration, shortTime } from './scheduleTiming';
import {
  DEFAULT_BREAK,
  DEFAULT_START,
  atATimeLine,
  buildBoard,
  csvRows,
  danceLine,
  groupPool,
  itemMatches,
  itemMinutes,
  levelChips,
  levelLine,
  newId,
  savePayload,
  stageTimes,
  toCsv,
} from './scheduleModel';
import '../../../../Teacher/Oireachtas/oireachtas.css';
import '../../../Oireachtas/organizerOireachtas.css';
import './schedule.css';

const BACK = (id) => `/organizer-manage-feis?id=${id}&Manage=feis`;
const POOL = 'pool';
// Saves of one event's schedule run one after another, also across visits to
// the page: a visit's last save finishes before the next visit loads or saves
// (review R1-F1).
const writes = {};
const queueWrite = (eventId, run) => {
  const next = (writes[eventId] || Promise.resolve()).then(run, run);
  writes[eventId] = next.catch(() => {});
  return next;
};

const SAVE_AFTER_MS = 800; // a change saves this long after the last one
const RETRY_MS = 5000; // a failed save tries again after this
const UNDO_LIMIT = 50;
const COALESCE_MS = 1500; // typing in one field is one undo step
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])';
// controls hidden by CSS (the phone-only Move to on a computer) are left out
// of the panel's focus loop (review R1-F4)
const visibleFocusables = (root) => [...root.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);

// The stage menu is drawn fixed under its stage header, outside the scrolling
// stage area that would clip it (web review R1-F4).
const menuPlace = (header) => {
  const r = header?.getBoundingClientRect();
  return r ? { top: r.top + 44, left: r.left + 8, width: Math.max(220, r.width - 16) } : null;
};


const api = () => process.env.REACT_APP_BASE_URL;
const messageOf = (err) => err?.messages || err?.message || 'Something went wrong. Please try again.';

// Material Symbols are ligature text: hidden from screen readers
const Msr = ({ children }) => (
  <span className="msr" aria-hidden="true">
    {children}
  </span>
);

const dancersOf = (c) => (Array.isArray(c?.dancers) ? c.dancers.length : 0);

// One competition or break: code / start / minutes, then level / dance and
// age (dancers) / dancers at a time. On the To schedule list there is no start.
const RowBody = ({ item, comp, time, minutes, pool }) => {
  if (item.type === 'break') {
    const notes = [item.fixed ? 'fixed start' : '', time?.gap ? `${time.gap} min gap before` : ''].filter(Boolean).join(' · ');
    return (
      <>
        <div className="sch-lc">
          <span className="sch-code" />
          {!pool && <span className={`sch-tm${item.fixed ? ' fixed' : ''}`}>{shortTime(time.start)}</span>}
          <span className="sch-du">{shortDuration(minutes)}</span>
        </div>
        <div className="sch-rc">
          <div className="sch-lv">Break</div>
          <div className="sch-nm">{item.label || 'Break'}</div>
          {(notes || time?.overlap > 0) && (
            <div className={`sch-mt${time?.overlap > 0 ? ' warn' : ''}`}>
              {time?.overlap > 0 ? `Starts ${time.overlap} min before the one before ends` : notes}
            </div>
          )}
        </div>
      </>
    );
  }
  const problem = item.problem === 'missing' ? 'Merged away or deleted · remove' : item.problem === 'empty' ? 'No dancers left · remove' : '';
  const est = comp && !item.problem ? estimate(comp, item.dancerCount) : null;
  const extra = [time?.gap ? `${time.gap} min gap before` : '', time?.overlap > 0 ? `starts ${time.overlap} min before the one before ends` : ''].filter(Boolean);
  return (
    <>
      <div className="sch-lc">
        <span className="sch-code">{comp?.code || item.code}</span>
        {!pool && <span className={`sch-tm${item.fixed ? ' fixed' : ''}`}>{shortTime(time.start)}</span>}
        <span className="sch-du">{shortDuration(minutes)}</span>
      </div>
      <div className="sch-rc">
        <div className="sch-lv">{comp ? levelLine(comp) : ' '}</div>
        <div className="sch-nm">
          {comp ? danceLine(comp) : item.code}
        </div>
        {problem ? (
          <div className="sch-mt warn">
            <Msr>error</Msr> {problem}
          </div>
        ) : (
          <div className={`sch-mt${time?.overlap > 0 ? ' warn' : ''}`}>{[atATimeLine(comp, est), ...extra].join(' · ')}</div>
        )}
      </div>
      {/* the dancer count, large, in its own column (Devin 2026-10-07) */}
      {comp && !item.problem && (
        <div className="sch-num">
          <b>{dancersOf(comp)}</b>
        </div>
      )}
    </>
  );
};

// Where Save used to be: Saved / Saving… / Couldn't save (it keeps trying)
const SaveStatus = ({ state, dirty, onRetry, short }) => {
  if (state === 'error') {
    return (
      <span className="sch-state is-error" role="status">
        <Msr>error</Msr>
        {short ? 'Not saved' : "Couldn't save, trying again"}
        <button type="button" className="sch-link" onClick={onRetry}>
          Retry
        </button>
      </span>
    );
  }
  const busy = state === 'saving' || dirty;
  return (
    <span className={`sch-state${busy ? '' : ' is-saved'}`} role="status">
      <Msr>{busy ? 'sync' : 'cloud_done'}</Msr>
      {busy ? 'Saving…' : 'Saved'}
    </span>
  );
};

const Stepper = ({ value, min = 1, max = 9, step = 1, onChange, label }) => (
  <span className="sch-stepper" role="group" aria-label={label}>
    <button type="button" aria-label={`Fewer: ${label}`} disabled={value <= min} onClick={() => onChange(Math.max(min, value - step))}>
      −
    </button>
    <b>{value}</b>
    <button type="button" aria-label={`More: ${label}`} disabled={value >= max} onClick={() => onChange(Math.min(max, value + step))}>
      +
    </button>
  </span>
);

const ManageSchedule = () => {
  useMaterialSymbols();
  const navigate = useNavigate();
  const { search } = useLocation();
  const eventId = new URLSearchParams(search).get('id');

  const [event, setEvent] = useState(null);
  const [comps, setComps] = useState({});
  const [stages, setStages] = useState([]);
  const [pool, setPool] = useState([]); // competition ids not on a stage
  const [dirty, setDirty] = useState(false); // changes not saved yet
  const [saveState, setSaveState] = useState('saved'); // saved | saving | error
  const [undo, setUndo] = useState([]); // earlier { stages, pool }, newest last
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null); // { where: stage id | 'pool', key }
  const [menuAt, setMenuAt] = useState(null); // where the open stage menu is drawn
  const menuAnchor = useRef(null); // the stage header it hangs from
  const [menu, setMenu] = useState(null); // stage id whose menu is open
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [view, setView] = useState(null); // phone: 'pool' or a stage id
  const pageRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      await writes[eventId]; // a save from the last visit lands first (review R1-F1)
      const [ev, list, sched] = await Promise.all([
        serviceConsumer('GET', `${api()}/feis/${eventId}`),
        serviceConsumer('GET', `${api()}/schedule-maker/list-competition/${eventId}`),
        serviceConsumer('POST', `${api()}/schedule-maker/list-schedules`, { eventId }),
      ]);
      const board = buildBoard(list?.data?.competitions || [], sched?.result || []);
      setEvent(ev?.Feis || null);
      setComps(board.comps);
      setStages(board.stages);
      setPool(board.pool.map((c) => String(c._id)));
      setDirty(false);
      setSaveState('saved');
      setUndo([]);
      setSelected(null);
      setView((v) => (v && (v === POOL || board.stages.some((s) => s.id === v)) ? v : board.stages[0] ? board.stages[0].id : POOL));
    } catch (err) {
      setLoadError(messageOf(err));
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) load();
  }, [eventId, load]);

  // the body takes the page's background while this page is open
  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;
    const before = document.body.style.backgroundColor;
    document.body.style.backgroundColor = getComputedStyle(page).backgroundColor;
    return () => {
      document.body.style.backgroundColor = before;
    };
  }, []);

  // ---- autosave -------------------------------------------------------------
  // Every change saves itself a moment after the last one; one save at a time,
  // and a failed save keeps trying. The board is the source of truth, so a
  // save never reloads it (that would break a drag or the open panel).
  const latest = useRef({ stages: [], comps: {} });
  latest.current = { stages, comps };
  const inFlight = useRef(false);
  const again = useRef(false);
  const timer = useRef(null);
  const alive = useRef(true); // a page that has gone makes one last save, no retries
  const saveNow = useCallback(async () => {
    clearTimeout(timer.current);
    if (inFlight.current) {
      again.current = true;
      return;
    }
    inFlight.current = true;
    setSaveState('saving');
    const { stages: board, comps: live } = latest.current;
    try {
      if (board.length) {
        const body = savePayload(eventId, board, live);
        await queueWrite(eventId, () => serviceConsumer('POST', `${api()}/schedule-maker/update-schedule`, body));
      }
      // saved what was on the board when the save started: still dirty if it changed since
      setDirty((d) => (latest.current.stages === board ? false : d));
      setSaveState('saved');
    } catch (err) {
      setSaveState('error');
      if (alive.current) timer.current = setTimeout(() => saveNow(), RETRY_MS);
    } finally {
      inFlight.current = false;
      // a page that has gone queued its last save itself (below)
      if (again.current && alive.current) {
        again.current = false;
        saveNow();
      }
    }
  }, [eventId]);
  useEffect(() => {
    if (!dirty) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => saveNow(), SAVE_AFTER_MS);
    return () => clearTimeout(timer.current);
  }, [dirty, stages, saveNow]);
  // leaving the page with a change not yet saved: save it now (the request
  // finishes after the page has gone)
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
      again.current = false;
      // The last save goes straight into the event's write queue, behind any
      // save still running, so the next visit's load waits for all of it
      // (review R1-F1, round 2). No retries once the page has gone.
      const { stages: board, comps: live } = latest.current;
      if (dirtyRef.current && board.length) {
        const body = savePayload(eventId, board, live);
        queueWrite(eventId, () => serviceConsumer('POST', `${api()}/schedule-maker/update-schedule`, body)).catch(() => {});
      }
    };
  }, [eventId]);
  // closing the tab while a save is pending or failing asks first
  useEffect(() => {
    if (!dirty && saveState !== 'error') return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, saveState]);

  // the fixed stage menu follows its stage header when anything scrolls or the
  // window resizes
  useEffect(() => {
    if (!menu) return undefined;
    const follow = () => setMenuAt(menuPlace(menuAnchor.current));
    window.addEventListener('resize', follow);
    window.addEventListener('scroll', follow, true);
    return () => {
      window.removeEventListener('resize', follow);
      window.removeEventListener('scroll', follow, true);
    };
  }, [menu]);

  // Escape closes the timing panel or a stage menu
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setSelected(null);
      setMenu(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const times = useMemo(() => Object.fromEntries(stages.map((s) => [s.id, stageTimes(s, comps)])), [stages, comps]);

  // ---- changes and Undo ------------------------------------------------------
  // Each change first keeps the board and the To schedule list as they were,
  // for Undo. Typing in one field (a name, a time) is one step; several
  // updates from one action are one step. Adding or deleting a stage happens
  // on the server at once, so it starts a new Undo history.
  const before = useRef({ stages: [], pool: [] });
  before.current = { stages, pool };
  const lastStep = useRef({ key: null, at: 0, tick: null });
  const tick = useRef(0);
  useEffect(() => {
    tick.current += 1; // one render = one action
  });
  const snapshot = (coalesce) => {
    const now = Date.now();
    const last = lastStep.current;
    const sameAction = last.tick === tick.current;
    const sameField = coalesce && last.key === coalesce && now - last.at < COALESCE_MS;
    lastStep.current = { key: coalesce || null, at: now, tick: tick.current };
    if (sameAction || sameField) return;
    const snap = before.current;
    setUndo((u) => [...u, snap].slice(-UNDO_LIMIT));
  };
  const change = (fn, coalesce) => {
    snapshot(coalesce);
    setStages((prev) => fn(prev));
    setDirty(true);
  };
  const changeItem = (stageId, key, patch, coalesce) =>
    change((prev) => prev.map((s) => (s.id !== stageId ? s : { ...s, items: s.items.map((it) => (it.key === key ? { ...it, ...patch } : it)) })), coalesce);
  const undoLast = () => {
    if (!undo.length || saving) return; // not while a stage is added or deleted (review R1-F2)
    const prev = undo[undo.length - 1];
    setUndo((u) => u.slice(0, -1));
    lastStep.current = { key: null, at: 0, tick: null };
    setStages(prev.stages);
    setPool(prev.pool);
    setSelected(null);
    setMenu(null);
    setDirty(true);
  };
  // Ctrl/Cmd+Z, outside a text field
  const undoRef = useRef(undoLast);
  undoRef.current = undoLast;
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || String(e.key).toLowerCase() !== 'z') return;
      if (e.target.closest && e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      e.preventDefault();
      undoRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const compItem = (id) => ({ key: `comp-${id}`, type: 'competition', competitionId: id, code: comps[id]?.code || '', fixed: false, fixedAt: null });

  // ---- moving things -------------------------------------------------------
  const moveItem = (fromId, key, toId, toIndex) => {
    change((prev) => {
      const moving = prev.find((s) => s.id === fromId)?.items.find((it) => it.key === key);
      if (!moving) return prev;
      const without = prev.map((s) => (s.id !== fromId ? s : { ...s, items: s.items.filter((it) => it.key !== key) }));
      return without.map((s) => {
        if (s.id !== toId) return s;
        const items = [...s.items];
        items.splice(toIndex == null ? items.length : toIndex, 0, moving);
        return { ...s, items };
      });
    });
    if (selected?.key === key) setSelected({ where: toId, key });
  };

  const unschedule = (stageId, key) => {
    const item = stages.find((s) => s.id === stageId)?.items.find((it) => it.key === key);
    if (!item) return;
    change((prev) => prev.map((s) => (s.id !== stageId ? s : { ...s, items: s.items.filter((it) => it.key !== key) })));
    if (item.type === 'competition' && !item.problem && comps[item.competitionId]) {
      setPool((p) => (p.includes(item.competitionId) ? p : [...p, item.competitionId]));
    }
    setSelected(null);
  };

  const schedule = (compId, stageId, index) => {
    if (!stages.some((s) => s.id === stageId)) return;
    setPool((p) => p.filter((id) => id !== compId));
    change((prev) =>
      prev.map((s) => {
        if (s.id !== stageId) return s;
        const items = [...s.items];
        items.splice(index == null ? items.length : index, 0, compItem(compId));
        return { ...s, items };
      })
    );
    setSelected(null);
  };

  const onDragEnd = ({ source, destination, draggableId }) => {
    if (!destination) return;
    const from = source.droppableId;
    const to = destination.droppableId;
    if (from === POOL && to === POOL) return;
    if (from === POOL) {
      schedule(draggableId.replace(/^comp-/, ''), to, destination.index);
      return;
    }
    if (to === POOL) {
      unschedule(from, draggableId);
      return;
    }
    if (from === to && source.index === destination.index) return;
    moveItem(from, draggableId, to, destination.index);
  };

  // ---- stages -------------------------------------------------------------
  const addStage = async () => {
    const used = new Set(stages.map((s) => s.name.trim().toLowerCase()));
    let n = stages.length + 1;
    while (used.has(`stage ${n}`)) n += 1;
    try {
      setSaving(true);
      // the order is renumbered with the next save
      const res = await serviceConsumer('POST', `${api()}/schedule-maker/create-stage`, {
        eventId,
        data: [{ stage: stages.length + 1, stageName: `Stage ${n}` }],
      });
      const created = res?.result?.[0];
      if (!created?._id) throw new Error('The stage was not created. Please try again.');
      setStages((prev) => [...prev, { id: String(created._id), name: `Stage ${n}`, startMin: DEFAULT_START, items: [] }]);
      setUndo([]); // the stage exists on the server now
      lastStep.current = { key: null, at: 0, tick: null }; // a fresh history starts a fresh step (review R1-F3)
      setDirty(true);
      setView(String(created._id));
    } catch (err) {
      ShowToastMessage('error', messageOf(err));
    } finally {
      setSaving(false);
    }
  };

  // deleted on the server at once (after the confirm); its competitions go
  // back to To schedule; it cannot be undone, so Undo starts again from here
  const deleteStage = async (stageId) => {
    const stage = stages.find((s) => s.id === stageId);
    if (!stage) return;
    try {
      setSaving(true);
      await serviceConsumer('DELETE', `${api()}/schedule-maker/delete-stage/${stageId}`);
    } catch (err) {
      ShowToastMessage('error', messageOf(err));
      return;
    } finally {
      setSaving(false);
    }
    // what is on the stage now, not when the delete started (review R1-F2)
    const now = before.current.stages.find((s) => s.id === stageId) || { items: [] };
    const back = now.items.filter((it) => it.type === 'competition' && !it.problem && comps[it.competitionId]).map((it) => it.competitionId);
    setPool((p) => [...p, ...back.filter((id) => !p.includes(id))]);
    setStages((prev) => prev.filter((s) => s.id !== stageId));
    setUndo([]);
    lastStep.current = { key: null, at: 0, tick: null }; // review R1-F3
    setDirty(true); // the other stages' order
    setMenu(null);
    setConfirmDelete(false);
    if (selected?.where === stageId) setSelected(null);
    setView(POOL);
  };

  const renameStage = (stageId, name) => change((prev) => prev.map((s) => (s.id === stageId ? { ...s, name } : s)), `name-${stageId}`);
  const setStageStart = (stageId, value) => {
    const t = parseTime(value);
    if (t == null) return;
    change((prev) => prev.map((s) => (s.id === stageId ? { ...s, startMin: t } : s)), `start-${stageId}`);
  };

  const targetStage = () => (selected && selected.where !== POOL ? selected.where : view && view !== POOL ? view : stages[0]?.id);

  const addBreak = () => {
    const stageId = targetStage();
    if (!stageId) {
      ShowToastMessage('error', 'Add a stage first.');
      return;
    }
    const breakId = newId('break');
    change((prev) =>
      prev.map((s) =>
        s.id !== stageId ? s : { ...s, items: [...s.items, { key: breakId, type: 'break', breakId, label: 'Break', minutes: DEFAULT_BREAK, fixed: false, fixedAt: null }] }
      )
    );
    setSelected({ where: stageId, key: breakId });
    setView(stageId);
  };

  // ---- save, export, upload ----------------------------------------------
  const exportCsv = () => {
    const blob = new Blob([`﻿${toCsv(csvRows(stages, comps))}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Schedule - ${String(event?.name || 'event').replace(/[\\/:*?"<>|]/g, '')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  };

  // the uploaded schedule families see (Feis.schedulePdfUrl / Name)
  const savePdf = async (url, name) => {
    await serviceConsumer('PUT', `${api()}/feis/${eventId}`, { schedulePdfUrl: url, schedulePdfName: name });
    setEvent((e) => ({ ...(e || {}), schedulePdfUrl: url, schedulePdfName: name }));
    ShowToastMessage('success', 'Schedule PDF uploaded');
  };
  const onPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') {
      ShowToastMessage('error', 'Please upload a PDF.');
      return;
    }
    try {
      setSaving(true);
      const form = new FormData();
      form.append('image', file);
      const res = await serviceConsumer('POST', `${api()}/feis/upload`, form);
      if (!res?.url) throw new Error('The PDF did not upload. Please try again.');
      await savePdf(res.url, res.name || file.name);
    } catch (err) {
      ShowToastMessage('error', messageOf(err));
    } finally {
      setSaving(false);
    }
  };

  // anything not yet saved is saved on the way out (autosave, above)
  const back = () => navigate(BACK(eventId));

  // ---- derived -----------------------------------------------------------
  const poolComps = useMemo(
    () =>
      pool
        .map((id) => comps[id])
        .filter(Boolean)
        .filter((c) => (filter === 'all' || (levelLine(c) || 'Other') === filter) && itemMatches({ type: 'competition' }, c, query)),
    [pool, comps, filter, query]
  );
  const poolGroups = useMemo(() => groupPool(poolComps), [poolComps]);
  const chips = useMemo(() => levelChips(Object.values(comps), pool.map((id) => comps[id]).filter(Boolean)), [comps, pool]);
  const poolMinutes = useMemo(() => pool.reduce((sum, id) => sum + (comps[id] ? estimate(comps[id]).minutes : 0), 0), [pool, comps]);
  const totalComps = Object.keys(comps).length;

  const subtitle = event
    ? [
        event.name,
        event.feis_date && formatDate(event.feis_date).toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' }),
        `${stages.length} stage${stages.length === 1 ? '' : 's'}`,
        `${totalComps} competition${totalComps === 1 ? '' : 's'}`,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  // the item in the timing panel
  const sel = useMemo(() => {
    if (!selected) return null;
    if (selected.where === POOL) {
      const c = comps[selected.key];
      return c ? { where: POOL, comp: c, item: { key: `comp-${selected.key}`, type: 'competition', competitionId: selected.key } } : null;
    }
    const stage = stages.find((s) => s.id === selected.where);
    const index = stage ? stage.items.findIndex((it) => it.key === selected.key) : -1;
    if (index < 0) return null;
    const item = stage.items[index];
    return {
      where: stage.id,
      stage,
      item,
      index,
      comp: item.type === 'competition' ? comps[item.competitionId] || item.detail : null,
      time: times[stage.id]?.[index],
    };
  }, [selected, stages, comps, times]);

  const q = query.trim();
  const hit = (item) => q && itemMatches(item, item.type === 'competition' ? comps[item.competitionId] : null, q);

  // Enter opens a row's timing (the rows are drag handles; Space drags)
  const openOnEnter = (e, target) => {
    if (e.key !== 'Enter' || e.target !== e.currentTarget) return;
    e.preventDefault();
    setSelected(target);
  };

  // The timing panel is modal for keyboards too (web review R1-F5): focus
  // moves into it, Tab stays inside, the page behind is inert, and closing
  // returns focus to the row that opened it.
  const panelRef = useRef(null);
  const panelOpen = Boolean(sel);
  const selectedKey = useRef(null);
  selectedKey.current = selected?.key || null;
  useEffect(() => {
    if (!panelOpen) return undefined;
    const opener = document.activeElement;
    const openedKey = selectedKey.current; // pool rows are keyed comp-<id>
    const page = pageRef.current;
    // the page behind the panel and everything beside the page (the header
    // too: web review R1-F5), each restored to how it was
    const inPage = page ? [...page.children].filter((el) => !el.classList.contains('sch-panel') && !el.classList.contains('sch-scrim')) : [];
    const beside = page?.parentElement ? [...page.parentElement.children].filter((el) => el !== page) : [];
    const behind = [...inPage, ...beside].map((el) => ({ el, inert: el.getAttribute('inert'), hidden: el.getAttribute('aria-hidden') }));
    behind.forEach(({ el }) => {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    });
    const first = panelRef.current ? visibleFocusables(panelRef.current)[0] : null;
    if (first) first.focus();
    const onKey = (e) => {
      if (e.key !== 'Tab' || !panelRef.current) return;
      const all = visibleFocusables(panelRef.current);
      if (!all.length) return;
      const head = all[0];
      const tail = all[all.length - 1];
      if (e.shiftKey && (document.activeElement === head || !panelRef.current.contains(document.activeElement))) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && (document.activeElement === tail || !panelRef.current.contains(document.activeElement))) {
        e.preventDefault();
        head.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      behind.forEach(({ el, inert, hidden }) => {
        if (inert == null) el.removeAttribute('inert');
        else el.setAttribute('inert', inert);
        if (hidden == null) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', hidden);
      });
      // back to the row that opened it (a click on a drag handle does not focus
      // it, so find the row by its key - it may have moved stage)
      const row = openedKey ? page?.querySelector(`.sch-row[data-key="${CSS.escape(openedKey)}"]`) || page?.querySelector(`.sch-row[data-key="${CSS.escape(`comp-${openedKey}`)}"]`) : null;
      const back = row || (opener && document.contains(opener) && opener !== document.body ? opener : null);
      if (back && typeof back.focus === 'function') back.focus();
    };
  }, [panelOpen]);

  const renderPanel = () => {
    if (!sel) return null;
    const { item, comp, where } = sel;
    const others = stages.filter((s) => s.id !== where);
    const isBreak = item.type === 'break';
    const est = comp && !item.problem && !isBreak ? estimate(comp, item.dancerCount) : null;
    const setK = (round, k) => {
      const base = est.parts.map((p) => ({ round: p.round, numberOfDancers: p.k }));
      changeItem(where, item.key, { dancerCount: base.map((d) => (d.round === round ? { ...d, numberOfDancers: k } : d)) });
    };
    const fixedValue = inputTime(item.fixed && Number.isFinite(item.fixedAt) ? item.fixedAt : sel.time?.start ?? DEFAULT_START);
    return (
      <div className="sch-panel" role="dialog" aria-modal="true" aria-label="Timing" ref={panelRef}>
        <button type="button" className="sch-x" aria-label="Close" onClick={() => setSelected(null)}>
          <Msr>close</Msr>
        </button>
        {isBreak ? (
          <>
            <div className="sch-pt">Break</div>
            {sel.time && (
              <div className="sch-psub">
                {sel.stage.name} · {clockTime(sel.time.start)} – {clockTime(sel.time.end)}
              </div>
            )}
            <label className="sch-field">
              <span>Name</span>
              <input type="text" maxLength={40} value={item.label} onChange={(e) => changeItem(where, item.key, { label: e.target.value }, `label-${item.key}`)} />
            </label>
            <div className="sch-field">
              <span>Length</span>
              <span className="sch-len">
                <Stepper label="break length" value={item.minutes} min={5} max={240} step={5} onChange={(v) => changeItem(where, item.key, { minutes: v })} />
                <span>{longDuration(item.minutes)}</span>
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="sch-pt">
              {comp?.code || item.code}
              {comp ? ` · ${levelLine(comp)}` : ''}
            </div>
            <div className="sch-psub">
              {comp ? `${danceLine(comp)} · ${dancersOf(comp)} dancer${dancersOf(comp) === 1 ? '' : 's'}` : ''}
              {est?.parts?.[0]?.teams ? ` · ${est.parts[0].teams} team${est.parts[0].teams === 1 ? '' : 's'}` : ''}
              {sel.time ? ` · ${sel.stage.name}, ${clockTime(sel.time.start)} – ${clockTime(sel.time.end)}` : ''}
            </div>
            {item.problem ? (
              <p className="sch-warn">
                {item.problem === 'missing'
                  ? 'This competition was merged into another or deleted after it was scheduled. Remove it: it is left out when you save.'
                  : 'This competition has no dancers left. Remove it: it is left out when you save.'}
              </p>
            ) : (
              est && (
                <>
                  <div className="sch-big">
                    <b>{longDuration(est.minutes)}</b>
                  </div>
                  <table className="sch-rounds">
                    <thead>
                      <tr>
                        <th>Round</th>
                        <th>Dance</th>
                        <th>At a time</th>
                        <th className="n">Groups</th>
                        <th className="n">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {est.parts.map((p) => (
                        <tr key={p.round}>
                          <td>{p.round}</td>
                          <td>
                            {p.dance}
                            <div className="sch-note">{p.music}</div>
                          </td>
                          <td>
                            {where === POOL ? (
                              p.k
                            ) : (
                              <Stepper label={`dancers at a time in round ${p.round}`} value={p.k} onChange={(k) => setK(p.round, k)} />
                            )}
                          </td>
                          <td className="n">{p.groups}</td>
                          <td className="n">{longDuration(p.sec / 60)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={4}>
                          <span className="sch-note">
                            Call and line-up{est.rounds > 1 ? `, ${est.rounds - 1} round change${est.rounds > 2 ? 's' : ''}` : ''}
                          </span>
                        </td>
                        <td className="n">{longDuration(est.overheadSec / 60)}</td>
                      </tr>
                    </tbody>
                  </table>
                </>
              )
            )}
          </>
        )}

        {where === POOL ? (
          // phone only: one column shows at a time, so it cannot be dragged
          // to a stage (Devin 2026-10-05: on a computer it is all drag)
          <div className="sch-field sch-phone-only">
            <span>Add to a stage</span>
            <div className="sch-acts">
              {stages.map((s) => (
                <button key={s.id} type="button" className="op-btn" onClick={() => schedule(String(comp._id), s.id)}>
                  {s.name}
                </button>
              ))}
              {!stages.length && <span className="sch-note">Add a stage first.</span>}
            </div>
          </div>
        ) : (
          <>
            {!item.problem && (
              <div className="sch-toggle">
                <label>
                  <input
                    type="checkbox"
                    checked={!!item.fixed}
                    onChange={(e) => changeItem(where, item.key, { fixed: e.target.checked, fixedAt: e.target.checked ? parseTime(fixedValue) : item.fixedAt })}
                  />
                  <span>
                    Start at a fixed time
                    <span className="sch-note">Otherwise it starts when the one before ends</span>
                  </span>
                </label>
                {item.fixed && (
                  <input
                    type="time"
                    aria-label="Fixed start time"
                    value={fixedValue}
                    onChange={(e) => {
                      const t = parseTime(e.target.value);
                      if (t != null) changeItem(where, item.key, { fixedAt: t }, `fixed-${item.key}`);
                    }}
                  />
                )}
              </div>
            )}
            <div className="sch-acts sch-pfoot">
              {!item.problem && others.length > 0 && (
                <label className="sch-move sch-phone-only">
                  <span>Move to</span>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) moveItem(where, item.key, e.target.value);
                    }}
                  >
                    <option value="">Stage…</option>
                    {others.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <button type="button" className="op-btn" onClick={() => unschedule(where, item.key)}>
                <Msr>{isBreak || item.problem ? 'delete' : 'undo'}</Msr>
                {isBreak || item.problem ? 'Remove' : 'Unschedule'}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const pdfName = event?.schedulePdfName || (event?.schedulePdfUrl ? 'schedule.pdf' : '');
  let poolIndex = 0;

  return (
    <>
      <HeaderOrg />
      <div className="oir op-page sch-page" ref={pageRef}>
        {(loading || saving) && <Loader />}
        <div className="wrap">
          <button type="button" className="op-back" onClick={back}>
            <Msr>arrow_back</Msr>Manage event
          </button>
          <div className="sch-ph1">
            <div>
              <div className="sch-h1" role="heading" aria-level={1}>
                Schedule
              </div>
              <div className="sch-sub">{subtitle || ' '}</div>
            </div>
            <div className="sch-top-acts">
              <SaveStatus state={saveState} dirty={dirty} onRetry={saveNow} />
              <button type="button" className="op-btn" onClick={undoLast} disabled={!undo.length || saving} title="Undo (Ctrl+Z)">
                <Msr>undo</Msr>Undo
              </button>
              <button type="button" className="op-btn" onClick={exportCsv} disabled={!stages.length}>
                <Msr>download</Msr>Export CSV
              </button>
              <button type="button" className="op-btn" onClick={() => fileRef.current?.click()}>
                <Msr>upload_file</Msr>Upload PDF
              </button>
            </div>
          </div>
          <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={onPdf} data-testid="schedule-pdf-input" />

          <div className="sch-pdf">
            <Msr>picture_as_pdf</Msr>
            {event?.schedulePdfUrl ? (
              <span>
                Families see <b>{pdfName}</b> ·{' '}
                <a href={event.schedulePdfUrl} target="_blank" rel="noopener noreferrer">
                  View
                </a>{' '}
                ·{' '}
                <button type="button" className="sch-link" onClick={() => fileRef.current?.click()}>
                  Replace
                </button>
              </span>
            ) : (
              <span>
                No schedule PDF yet: families see “Schedule not posted” ·{' '}
                <button type="button" className="sch-link" onClick={() => fileRef.current?.click()}>
                  Upload PDF
                </button>
              </span>
            )}
          </div>

          <div className="sch-bar">
            <label className="sch-search">
              <Msr>search</Msr>
              <input
                type="text"
                value={query}
                autoComplete="off"
                spellCheck={false}
                placeholder="Find a competition: code, dance, age"
                aria-label="Find a competition"
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button type="button" className="sch-clear" aria-label="Clear search" onClick={() => setQuery('')}>
                  <Msr>close</Msr>
                </button>
              )}
            </label>
            <button type="button" className="op-btn" onClick={addBreak} disabled={!stages.length}>
              <Msr>coffee</Msr>Add break
            </button>
            <button type="button" className="op-btn" onClick={addStage} disabled={saving}>
              <Msr>add</Msr>Add stage
            </button>
          </div>

          {loadError && (
            <div className="sch-empty">
              <Msr>error</Msr>
              <div className="sch-empty-t">The schedule didn’t load</div>
              <div className="sch-empty-s">{loadError}</div>
              <button type="button" className="op-btn" onClick={load}>
                Try again
              </button>
            </div>
          )}

          <div className="sch-pills" role="tablist" aria-label="Show">
            <button type="button" role="tab" aria-selected={view === POOL} className={`sch-pill${view === POOL ? ' on' : ''}`} onClick={() => setView(POOL)}>
              To schedule <span className="n">{pool.length}</span>
            </button>
            {stages.map((s) => {
              const t = times[s.id] || [];
              return (
                <button key={s.id} type="button" role="tab" aria-selected={view === s.id} className={`sch-pill${view === s.id ? ' on' : ''}`} onClick={() => setView(s.id)}>
                  {s.name}
                  {t.length > 0 && <span className="n">ends {shortTime(t[t.length - 1].end)}</span>}
                </button>
              );
            })}
          </div>

          {!loadError && (
            <DragDropContext onDragEnd={onDragEnd}>
              <div className="sch-board">
                <Droppable droppableId={POOL}>
                  {(provided, snapshot) => (
                    <div className={`sch-col sch-pool${view === POOL ? ' active' : ''}${snapshot.isDraggingOver ? ' over' : ''}`}>
                      <div className="sch-colh">
                        <div className="sch-colt">
                          <span className="sch-name">
                            <Msr>inbox</Msr>To schedule
                          </span>
                          <span className={`sch-count${pool.length ? '' : ' done'}`}>{pool.length ? `${pool.length} left` : 'All scheduled'}</span>
                        </div>
                        <div className="sch-filters">
                          <button type="button" className={`sch-fchip${filter === 'all' ? ' on' : ''}`} onClick={() => setFilter('all')}>
                            All <span className="n">{pool.length}</span>
                          </button>
                          {chips.map((f) => (
                            <button
                              key={f.key}
                              type="button"
                              className={`sch-fchip${filter === f.key ? ' on' : ''}${f.left ? '' : ' none-left'}`}
                              onClick={() => setFilter(filter === f.key ? 'all' : f.key)}
                              aria-pressed={filter === f.key}
                            >
                              {f.label} <span className="n">{f.left}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="sch-list" ref={provided.innerRef} {...provided.droppableProps}>
                        {poolGroups.map((g) => (
                          <React.Fragment key={g.title}>
                            <div className="sch-grp">{g.title}</div>
                            {g.comps.map((c) => {
                              const id = String(c._id);
                              const index = poolIndex;
                              poolIndex += 1;
                              return (
                                <Draggable key={id} draggableId={`comp-${id}`} index={index}>
                                  {(p) => (
                                    <div
                                      ref={p.innerRef}
                                      {...p.draggableProps}
                                      {...p.dragHandleProps}
                                      data-key={`comp-${id}`}
                                      className={`sch-row${selected?.where === POOL && selected.key === id ? ' sel' : ''}`}
                                      aria-label={`${c.code}: open its timing`}
                                      onClick={() => setSelected({ where: POOL, key: id })}
                                      onKeyDown={(e) => openOnEnter(e, { where: POOL, key: id })}
                                    >
                                      <RowBody item={{ type: 'competition', code: c.code }} comp={c} minutes={estimate(c).minutes} pool />
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                          </React.Fragment>
                        ))}
                        {provided.placeholder}
                        {!poolComps.length && <div className="sch-none">{pool.length ? 'Nothing matches.' : 'Everything is on a stage.'}</div>}
                      </div>
                    </div>
                  )}
                </Droppable>

                <div className="sch-stages">
                  {stages.map((s) => {
                    const t = times[s.id] || [];
                    const count = s.items.filter((it) => it.type === 'competition' && !it.problem).length;
                    return (
                      <Droppable key={s.id} droppableId={s.id}>
                        {(provided, snapshot) => (
                          <div className={`sch-col${view === s.id ? ' active' : ''}${snapshot.isDraggingOver ? ' over' : ''}`} data-stage={s.name}>
                            <div className="sch-colh">
                              <div className="sch-colt">
                                <span className="sch-name">{s.name}</span>
                                <button
                                  type="button"
                                  className="sch-more"
                                  aria-label={`${s.name} options`}
                                  onClick={(e) => {
                                    menuAnchor.current = e.currentTarget.closest('.sch-colh');
                                    setMenuAt(menuPlace(menuAnchor.current));
                                    setMenu(menu === s.id ? null : s.id);
                                    setConfirmDelete(false);
                                  }}
                                >
                                  <Msr>more_horiz</Msr>
                                </button>
                              </div>
                              <div className="sch-span">
                                <b>{clockTime(t[0]?.start ?? s.startMin)}</b>
                                {t.length > 0 && (
                                  <>
                                    {' – '}
                                    <b>{clockTime(t[t.length - 1].end)}</b>
                                  </>
                                )}
                                {` · ${count} competition${count === 1 ? '' : 's'}`}
                              </div>
                              {menu === s.id && (
                                <div className="sch-menu" style={menuAt ? { position: 'fixed', top: menuAt.top, left: menuAt.left, right: 'auto', width: menuAt.width } : undefined}>
                                  <label className="sch-field">
                                    <span>Name</span>
                                    <input type="text" maxLength={60} value={s.name} onChange={(e) => renameStage(s.id, e.target.value)} />
                                  </label>
                                  <label className="sch-field">
                                    <span>Starts at</span>
                                    <input type="time" value={inputTime(s.startMin)} onChange={(e) => setStageStart(s.id, e.target.value)} />
                                  </label>
                                  {confirmDelete ? (
                                    <div className="sch-acts">
                                      <span className="sch-note">Deletes the stage now; its competitions go back to To schedule.</span>
                                      <button type="button" className="op-btn sch-danger" onClick={() => deleteStage(s.id)}>
                                        Delete {s.name}
                                      </button>
                                      <button type="button" className="op-btn" onClick={() => setConfirmDelete(false)}>
                                        Keep
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="sch-acts">
                                      <button type="button" className="op-btn" onClick={() => setConfirmDelete(true)}>
                                        <Msr>delete</Msr>Delete stage
                                      </button>
                                      <button type="button" className="op-btn" onClick={() => setMenu(null)}>
                                        Done
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="sch-list" ref={provided.innerRef} {...provided.droppableProps}>
                              {s.items.map((item, i) => (
                                <Draggable key={item.key} draggableId={item.key} index={i}>
                                  {(p) => (
                                    <div
                                      ref={p.innerRef}
                                      {...p.draggableProps}
                                      {...p.dragHandleProps}
                                      data-key={item.key}
                                      className={[
                                        'sch-row',
                                        item.type === 'break' ? 'brk' : '',
                                        item.problem ? 'bad' : '',
                                        selected?.where === s.id && selected.key === item.key ? 'sel' : '',
                                        hit(item) ? 'hit' : '',
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                      onClick={() => setSelected({ where: s.id, key: item.key })}
                                      onKeyDown={(e) => openOnEnter(e, { where: s.id, key: item.key })}
                                    >
                                      <RowBody
                                        item={item}
                                        comp={item.type === 'competition' ? comps[item.competitionId] || item.detail : null}
                                        time={t[i]}
                                        minutes={itemMinutes(item, comps)}
                                      />
                                      {/* hover ×: back to To schedule (a break or a flagged row is removed) */}
                                      <button
                                        type="button"
                                        className="sch-rowx"
                                        aria-label={
                                          item.type === 'break' || item.problem
                                            ? `Remove ${item.type === 'break' ? item.label || 'break' : item.code || 'row'}`
                                            : `Unschedule ${item.code || comps[item.competitionId]?.code || ''}`
                                        }
                                        title={item.type === 'break' || item.problem ? 'Remove' : 'Back to To schedule'}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          unschedule(s.id, item.key);
                                        }}
                                        onKeyDown={(e) => e.stopPropagation()}
                                      >
                                        <Msr>close</Msr>
                                      </button>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                              {!s.items.length && <div className="sch-none">Drag competitions here</div>}
                            </div>
                          </div>
                        )}
                      </Droppable>
                    );
                  })}
                  {!stages.length && !loading && (
                    <div className="sch-empty">
                      <Msr>view_column</Msr>
                      <div className="sch-empty-t">No stages yet</div>
                      <div className="sch-empty-s">Add a stage, then drag competitions onto it.</div>
                    </div>
                  )}
                </div>
              </div>
            </DragDropContext>
          )}
        </div>

        {sel && (
          <>
            <div className="sch-scrim" onClick={() => setSelected(null)} />
            {renderPanel()}
          </>
        )}

        <div className="sch-dock">
          <SaveStatus state={saveState} dirty={dirty} onRetry={saveNow} short />
          <button type="button" className="op-btn" onClick={undoLast} disabled={!undo.length || saving} aria-label="Undo">
            <Msr>undo</Msr>
          </button>
          <button type="button" className="op-btn" onClick={exportCsv} disabled={!stages.length} aria-label="Export CSV">
            <Msr>download</Msr>CSV
          </button>
          <button type="button" className="op-btn" onClick={() => fileRef.current?.click()} aria-label="Upload PDF">
            <Msr>upload_file</Msr>PDF
          </button>
        </div>
      </div>
    </>
  );
};

export default ManageSchedule;
